import { wrapper, createApiClient, Result, ApiError } from "../base";
import { LaravelPaginatedCollection } from "@/types/collections";
import ExportScheduleDO = App.Data.ExportScheduler.ExportScheduleDO;
import CreateExportScheduleDTO = App.Data.ExportScheduler.CreateExportScheduleDTO;
import ExportScheduleRecipientDO = App.Data.ExportScheduler.ExportScheduleRecipientDO;
import CreateDataTableExportScheduleDTO = App.Data.ExportScheduler.CreateDataTableExportScheduleDTO;
import CreateSegmentExportScheduleDTO = App.Data.ExportScheduler.CreateSegmentExportScheduleDTO;
import CreateDashboardExportScheduleDTO = App.Data.ExportScheduler.CreateDashboardExportScheduleDTO;
import ExportScheduleExecutionRecipientDO = App.Data.ExportScheduler.ExportScheduleExecutionRecipientDO;
import UpcomingExportScheduleDO = App.Data.ExportScheduler.UpcomingExportScheduleDO;
import RecentExportExecutionDO = App.Data.ExportScheduler.RecentExportExecutionDO;

const baseClient = createApiClient();

export default class {
	show(
		exportScheduleUuid: string
	): Promise<Result<ExportScheduleDO, ApiError>> {
		return wrapper(baseClient.get(`export-schedules/${exportScheduleUuid}`));
	}

	index(params: {
		page: number;
		itemsPerPage: number;
		sortBy: { key: string; order: "asc" | "desc" }[];
		search: string;
	}): Promise<Result<LaravelPaginatedCollection<ExportScheduleDO>, ApiError>> {
		let sortBy = undefined;
		if (params.sortBy.length > 0) {
			sortBy =
				params.sortBy[0].order === "desc"
					? `-${params.sortBy[0].key}`
					: params.sortBy[0].key;
		}
		return wrapper(
			baseClient.get("export-schedules", {
				params: {
					page: params.page,
					per_page: params.itemsPerPage,
					sort: sortBy,
					"filter[title]": params.search,
				},
			})
		);
	}

	create(
		schedule: CreateExportScheduleDTO
	): Promise<Result<ExportScheduleDO, ApiError>> {
		return wrapper(baseClient.post(`/export-schedules`, schedule));
	}

	createFromExplorer(
		schedule: CreateDataTableExportScheduleDTO
	): Promise<Result<ExportScheduleDO, ApiError>> {
		return wrapper(
			baseClient.post(`/export-schedules/create-from-explorer`, schedule)
		);
	}

	createFromSegmentBuilder(
		schedule: CreateSegmentExportScheduleDTO
	): Promise<Result<ExportScheduleDO, ApiError>> {
		return wrapper(
			baseClient.post(`/export-schedules/create-from-segment-builder`, schedule)
		);
	}

	createFromDashboardBuilder(
		schedule: CreateDashboardExportScheduleDTO
	): Promise<Result<ExportScheduleDO, ApiError>> {
		return wrapper(
			baseClient.post(
				`/export-schedules/create-from-dashboard-builder`,
				schedule
			)
		);
	}

	delete(uuid: string) {
		return wrapper<any>(baseClient.delete(`export-schedules/${uuid}`));
	}

	update(
		exportSchedule: ExportScheduleDO
	): Promise<Result<ExportScheduleDO, ApiError>> {
		return wrapper(
			baseClient.put(`/export-schedules/${exportSchedule.uuid}`, exportSchedule)
		);
	}

	getExportTypes(): Promise<
		Result<App.Data.ExportScheduler.ExportTypeDO[], ApiError>
	> {
		return wrapper(baseClient.get(`/export-schedules/export-types`));
	}

	deleteRecipient(
		exportSchedule: ExportScheduleDO,
		recipient: ExportScheduleRecipientDO
	) {
		return wrapper<any>(
			baseClient.delete(
				`export-schedules/${exportSchedule.uuid}/recipients/${recipient.id}`
			)
		);
	}

	createRecipient(
		exportSchedule: ExportScheduleDO,
		recipient: ExportScheduleRecipientDO
	): Promise<Result<ExportScheduleDO, ApiError>> {
		return wrapper(
			baseClient.post(
				`/export-schedules/${exportSchedule.uuid}/recipients`,
				recipient
			)
		);
	}

	getRecipients(
		params: {
			page: number;
			itemsPerPage: number;
			sortBy: { key: string; order: "asc" | "desc" }[];
			search: string;
		},
		exportSchedule: ExportScheduleDO
	): Promise<
		Result<
			LaravelPaginatedCollection<App.Data.ExportScheduler.ExportScheduleRecipientDO>,
			ApiError
		>
	> {
		let sortBy = undefined;
		if (params.sortBy.length > 0) {
			sortBy =
				params.sortBy[0].order === "desc"
					? `-${params.sortBy[0].key}`
					: params.sortBy[0].key;
		}
		return wrapper(
			baseClient.get(`/export-schedules/${exportSchedule.uuid}/recipients`, {
				params: {
					page: params.page,
					per_page: params.itemsPerPage,
					sort: sortBy,
					"filter[email]": params.search,
				},
			})
		);
	}

	getUpcomingExecutions(): Promise<
		Result<UpcomingExportScheduleDO[], ApiError>
	> {
		return wrapper(baseClient.get(`/export-schedules/upcoming-executions`));
	}

	getRecentExecutions(): Promise<Result<RecentExportExecutionDO[], ApiError>> {
		return wrapper(baseClient.get(`/export-schedules/recent-executions`));
	}

	downloadExport(
		recipient: ExportScheduleExecutionRecipientDO
	): Promise<Result<Blob, ApiError>> {
		return wrapper(
			baseClient.get(`/exports/${recipient.id}/downloadExport`, {
				responseType: "blob",
			})
		);
	}

	getExecutions(
		params: {
			page: number;
			itemsPerPage: number;
			sortBy: { key: string; order: "asc" | "desc" }[];
		},
		exportSchedule: ExportScheduleDO
	): Promise<
		Result<
			LaravelPaginatedCollection<App.Data.ExportScheduler.ExportScheduleExecutionDO>,
			ApiError
		>
	> {
		let sortBy = undefined;
		if (params.sortBy.length > 0) {
			sortBy =
				params.sortBy[0].order === "desc"
					? `-${params.sortBy[0].key}`
					: params.sortBy[0].key;
		}
		return wrapper(
			baseClient.get(`/export-schedules/${exportSchedule.uuid}/executions`, {
				params: {
					page: params.page,
					per_page: params.itemsPerPage,
					sort: sortBy,
				},
			})
		);
	}
}
